/**
 * Full-bleed hero: one background image under a configurable gradient overlay.
 *
 * Every value comes from hero_settings. The overlay is rendered from CSS custom
 * properties so admins get controlled options only — never free-form CSS.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  HERO_DEFAULTS,
  HERO_OVERLAY_BACKGROUND,
  heroCssVars,
  heroHeightCss,
  heroSrcSet,
  trackCtaClick,
} from '@/utils/heroStyles';

const ALIGN = {
  left: { wrapper: 'justify-start', text: 'text-left items-start' },
  center: { wrapper: 'justify-center', text: 'text-center items-center' },
  right: { wrapper: 'justify-end', text: 'text-right items-end' },
};

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/40';

const CtaButton = ({ cta, style, variant, location, mobile, testId }) => {
  const className = `inline-flex items-center justify-center font-semibold transition-all duration-200
    active:scale-[0.97] ${FOCUS_RING} ${mobile ? 'w-full px-6 py-3 text-base' : 'px-7 py-3.5 text-base'}
    ${variant === 'primary' ? 'shadow-lg hover:brightness-110 hover:shadow-xl' : 'backdrop-blur-sm hover:bg-white/15'}`;
  const props = {
    className,
    style,
    onClick: () => trackCtaClick(location),
    'data-testid': testId,
  };
  if (cta.new_tab) {
    return (
      <a href={cta.url} target="_blank" rel="noopener noreferrer" {...props}>
        {cta.label}
      </a>
    );
  }
  return (
    <Link to={cta.url} {...props}>
      {cta.label}
    </Link>
  );
};

export const HeroSection = ({ hero: heroProp, mobile = false, preview = false }) => {
  const hero = {
    ...HERO_DEFAULTS,
    ...(heroProp || {}),
    primary_cta: { ...HERO_DEFAULTS.primary_cta, ...(heroProp?.primary_cta || {}) },
    secondary_cta: { ...HERO_DEFAULTS.secondary_cta, ...(heroProp?.secondary_cta || {}) },
  };
  const align = ALIGN[hero.content_alignment] || ALIGN.left;
  const imageUrl = hero.image_url_full || hero.image_url;

  const primaryStyle = {
    backgroundColor: hero.cta_bg_color,
    color: hero.cta_text_color,
    borderRadius: `${hero.cta_radius}px`,
  };
  const secondaryStyle = {
    backgroundColor: 'transparent',
    color: hero.heading_color,
    border: `1.5px solid ${hero.heading_color}`,
    borderRadius: `${hero.cta_radius}px`,
  };

  return (
    <section
      className={`relative w-full overflow-hidden ${preview ? 'h-full' : ''}`}
      style={{
        ...heroCssVars(hero, { mobile }),
        ...(preview ? {} : { height: heroHeightCss(hero), minHeight: mobile ? '460px' : '520px' }),
      }}
      data-testid="hero-section"
    >
      <img
        src={imageUrl}
        srcSet={heroSrcSet(imageUrl, hero.image_widths)}
        sizes="100vw"
        alt={hero.image_alt}
        loading="eager"
        fetchPriority="high"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
        style={{ objectPosition: `${hero.focal_x}% ${hero.focal_y}%` }}
        data-testid="hero-image"
      />

      <div
        className="absolute inset-0"
        style={{ background: HERO_OVERLAY_BACKGROUND }}
        aria-hidden="true"
        data-testid="hero-overlay"
      />

      <div
        className={`relative z-10 flex h-full w-full ${align.wrapper} ${preview ? 'px-6' : 'container mx-auto px-6'}`}
      >
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className={`flex h-full max-w-2xl flex-col justify-center gap-6 ${align.text} ${
            preview ? 'py-8' : 'py-24'
          }`}
        >
          <h1
            className={`font-heading font-bold tracking-tight ${
              preview && mobile ? 'text-2xl' : preview ? 'text-3xl' : 'text-4xl sm:text-5xl lg:text-6xl'
            }`}
            style={{ color: hero.heading_color }}
            data-testid="hero-headline"
          >
            {hero.headline}
          </h1>

          {hero.subheadline && (
            <p
              className={`leading-relaxed ${preview ? 'text-sm' : 'text-sm sm:text-base md:text-lg'} max-w-xl`}
              style={{ color: hero.body_color }}
              data-testid="hero-subheadline"
            >
              {hero.subheadline}
            </p>
          )}

          <div className={`flex ${mobile ? 'flex-col' : 'flex-col sm:flex-row'} gap-3 ${mobile ? 'w-full' : ''}`}>
            {hero.primary_cta.show !== false && (
              <CtaButton
                cta={hero.primary_cta}
                style={primaryStyle}
                variant="primary"
                location="hero_primary"
                mobile={mobile}
                testId="hero-primary-cta"
              />
            )}
            {hero.secondary_cta.show && (
              <CtaButton
                cta={hero.secondary_cta}
                style={secondaryStyle}
                variant="secondary"
                location="hero_secondary"
                mobile={mobile}
                testId="hero-secondary-cta"
              />
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
